import React, { useCallback } from "react";
import { differenceInMinutes } from "date-fns";
import { RouteComponentProps } from "react-router-dom";
import { useApolloClient } from "@apollo/client";

import { useBookingsMapContext } from "../Context/BookingsMapContext";
import { useWorkplacesClient } from "../../../../api/grpc/workplaces/useWorkplacesClient";
import { useTimeZoneContext } from "../../../../lib/context/TimeZone/TimeZoneContext";
import useGrpcQuery from "../../../../lib/hooks/useGrpcQuery";

import { FLOOR_FIELDS_FRAGMENT } from "../../../../api/graphql/floors/floors";
import {
  FloorMapRequest,
  FloorMapResponse,
} from "../../../../api/grpc/workplaces/workplaces";
import { LoadingBox } from "../../../shared/LoadingBox/LoadingBox";
import { BookingsFloorMap } from "../BookingsFloorMap/BookingsFloorMap";
import { BookingsMapHeader } from "./BookingsMapHeader";

import "./BookingsMapSingle.scss";

interface Props {
  id: string;
}

export const BookingsMapSingle = ({
  match: {
    params: { id },
  },
}: RouteComponentProps<Props>) => {
  const client = useApolloClient();
  const { getFloorMap } = useWorkplacesClient();
  const { timeZone } = useTimeZoneContext();
  const { dateAndTime } = useBookingsMapContext();

  const floorData = client.readFragment({
    id: `Floor:${id}`,
    fragmentName: "FloorFields",
    fragment: FLOOR_FIELDS_FRAGMENT,
  });

  const getFloorMapWithAdjustedTime = useCallback(
    (variables: FloorMapRequest) => {
      const resolvedDateTime =
        differenceInMinutes(new Date(), new Date(variables.dateTime)) <= 0
          ? variables.dateTime
          : new Date().toISOString();

      return getFloorMap({
        ...variables,
        dateTime: resolvedDateTime,
      });
    },
    [getFloorMap]
  );

  const { data, loading, error, refetch } = useGrpcQuery<
    FloorMapRequest,
    FloorMapResponse
  >({
    method: getFloorMapWithAdjustedTime,
    variables: {
      customerId: "",
      floorId: id,
      dateTime: dateAndTime,
      timeZone: timeZone,
    },
    refetchOnWindowFocus: true,
    refetchOnWindowFocusThreshold: 30000,
  });

  return (
    <div className="BookingsMapSingle">
      <BookingsMapHeader title={floorData?.name} data={data} />

      {loading ? (
        <LoadingBox />
      ) : !!error ? (
        <div>Error fetching markers, please try again.</div>
      ) : (
        <BookingsFloorMap
          data={floorData}
          floorDataMarker={data}
          refetch={refetch}
        />
      )}
    </div>
  );
};
